export interface RateSnapshotListItem {
    rateSnapshotId: string;
    name: string;
    description: string | null;
    startDate: string;
    endDate: string;
    totalGrossAmount: number;
    createdByUserId: string;
    createdByUserName: string | null;
    createdUtc: string;
    archivedUtc: string | null;
    data: RateSnapshotData;
}

export interface RateSnapshotData {
    ratePlanId: string;
    ratePlanVersion: number;
    sourceStartDate: string;
    sourceEndDate: string;
    rooms: RoomRateSnapshot[];
    programs: ProgramRateSnapshot[];
}

export interface RoomRateSnapshot {
    reservationRoomId: string;
    roomType: string | null;
    roomTypeName: string | null;
    dayRates: DayRateSnapshot[];
}

export interface ProgramRateSnapshot {
    reservationProgramId: string;
    programType: string;
    programTypeName: string | null;
    dayRates: DayRateSnapshot[];
}

import type { PriceBreakdownContainer } from '@digitalopsdev/serviceops-reservations-components';

export interface DayRateSnapshot {
    date: string;
    dayOffset: number;
    grossAmount: number;
    netAmount: number;
    serviceChargeAmount: number;
    vatAmount: number;
    evaluationSessionJson: string;
    overrideBaseRateJson: string | null;
    overrideRatePlanJson: string | null;
    minorAdjustment: number;
    /**
     * Full price breakdown (base + multipliers + discounts) in the same shape the
     * room/program price dialogs consume. Captured server-side at snapshot creation.
     */
    priceBreakdown: PriceBreakdownContainer | null;
}

// ── Cluster-based per-day override model ──────────────────────────────
//
// Each day in a cluster has 0 or 1 source. Wire enum is constant-case
// (ROOM / PROGRAM) to match the API's JsonStringEnumMemberConverter with
// ConstantCaseJsonNamingPolicy.

export type ClusterKind = 'ROOM' | 'PROGRAM';

export interface ClusterDayAssignment {
    clusterId: string;
    clusterKind: ClusterKind;
    memberIds: string[];
    days: SnapshotDayOverride[];
}

export interface SnapshotDayOverride {
    targetDate: string;
    snapshotItemIndex: number;
    snapshotDayOffset: number;
}

// ── Per-cluster date selection for snapshot creation ──────────────────

export interface ClusterDaySelection {
    clusterKind: ClusterKind;
    memberIds: string[];
    dates: string[];
}

// ── Preview result ────────────────────────────────────────────────────

export interface PreviewApplyResult {
    clusters: ClusterPreviewApply[];
    warnings: string[];
}

export interface ClusterPreviewApply {
    clusterId: string;
    clusterKind: ClusterKind;
    displayName: string;
    days: DayPreviewApply[];
}

export interface DayPreviewApply {
    date: string;
    currentGross: number;
    snapshotGross: number | null;
    sourceLabel: string | null;
}

export function getDisplayName(
    type: string | null,
    typeName: string | null,
): string {
    return typeName ?? type ?? 'Unknown';
}
